import React, { useRef, useState, useEffect } from 'react';
import { documentService, MedicalReport, MedicalReportsResponse } from '../services/documentService';

interface DocumentManagerProps {
  patientId: string | number;
}

interface UploadFormData {
  report_type: string;
  sub_type: string;
  description: string;
  selectedFile: File | null;
}

const DocumentManager: React.FC<DocumentManagerProps> = ({ patientId }) => {
  const [reports, setReports] = useState<MedicalReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFormData, setUploadFormData] = useState<UploadFormData>({
    report_type: 'medical',
    sub_type: '',
    description: '',
    selectedFile: null
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load medical reports on component mount
  useEffect(() => {
    loadMedicalReports();
  }, [patientId]);

  const loadMedicalReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: MedicalReportsResponse = await documentService.getMedicalReports(patientId);
      setReports(response.reports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load medical reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
      return;
    }

    try {
      await documentService.deleteMedicalReport(patientId, reportId);
      setReports(list => list.filter(report => report.id !== reportId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setUploadFormData(prev => ({
        ...prev,
        selectedFile: file
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadFormData.selectedFile) {
      setError('Por favor selecciona un archivo');
      return;
    }
    
    if (!uploadFormData.sub_type.trim() || !uploadFormData.description.trim()) {
      setError('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const newReport = await documentService.uploadMedicalReport(patientId, {
        file: uploadFormData.selectedFile,
        report_type: uploadFormData.report_type,
        sub_type: uploadFormData.sub_type,
        description: uploadFormData.description
      });
      
      setReports(list => [...list, newReport]);
      setShowUploadForm(false);
      setUploadFormData({
        report_type: 'medical',
        sub_type: '',
        description: '',
        selectedFile: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const handleFormInputChange = (field: keyof UploadFormData, value: string) => {
    setUploadFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Archivos adjuntos</div>
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          Cargando documentos...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Archivos adjuntos</div>
      
      {error && (
        <div style={{ 
          background: '#fed7d7', 
          color: '#c53030', 
          padding: '10px', 
          borderRadius: '5px', 
          marginBottom: '16px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {reports.map(report => (
          <li key={report.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, padding: '8px', background: '#f7fafc', borderRadius: '6px' }}>
            <div style={{ flex: 1 }}>
              <a 
                href={report.fileUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ color: '#3182ce', textDecoration: 'underline', fontWeight: 600 }}
              >
                {report.description || `${report.reportType} - ${report.subType}`}
              </a>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                {report.reportType} • {report.subType} • {new Date(report.uploadedAt).toLocaleDateString()}
              </div>
            </div>
            <button 
              onClick={() => handleDelete(report.id)} 
              style={{ 
                marginLeft: 10, 
                background: '#e53e3e', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 5, 
                padding: '4px 10px', 
                fontWeight: 600, 
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Eliminar
            </button>
          </li>
        ))}
      </ul>

      {reports.length === 0 && !loading && (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666',
          background: '#f7fafc',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          No hay documentos médicos disponibles
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {!showUploadForm ? (
          <button
            onClick={() => setShowUploadForm(true)}
            style={{ 
              background: '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: 'pointer' 
            }}
          >
            Subir documento
          </button>
        ) : (
          <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '6px' }}>
            <div style={{ fontWeight: 600, marginBottom: '12px' }}>Subir nuevo documento médico</div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '14px' }}>
                Tipo de reporte:
              </label>
              <select
                value={uploadFormData.report_type}
                onChange={(e) => handleFormInputChange('report_type', e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #cbd5e0', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="medical">Médico</option>
                <option value="lab">Laboratorio</option>
                <option value="imaging">Imagenología</option>
                <option value="prescription">Receta</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '14px' }}>
                Subtipo: *
              </label>
              <select
                value={uploadFormData.sub_type}
                onChange={(e) => handleFormInputChange('sub_type', e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #cbd5e0', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="">Selecciona una especialidad...</option>
                <option value="cardiology">Cardiology</option>
                <option value="clinical_analysis">Clinical Analysis</option>
                <option value="dentistry">Dentistry</option>
                <option value="dermatology">Dermatology</option>
                <option value="endocrinology">Endocrinology</option>
                <option value="gastroenterology">Gastroenterology</option>
                <option value="neurology">Neurology</option>
                <option value="ophthalmology">Ophthalmology</option>
                <option value="orthopedics">Orthopedics</option>
                <option value="pediatrics">Pediatrics</option>
                <option value="psychiatry">Psychiatry</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '14px' }}>
                Descripción: *
              </label>
              <textarea
                value={uploadFormData.description}
                onChange={(e) => handleFormInputChange('description', e.target.value)}
                placeholder="Describe el contenido del documento..."
                rows={3}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #cbd5e0', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, fontSize: '14px' }}>
                Archivo: *
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #cbd5e0', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {uploadFormData.selectedFile && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '4px',
                  padding: '4px 8px',
                  background: '#f0fff4',
                  borderRadius: '4px',
                  border: '1px solid #9ae6b4'
                }}>
                  ✓ Archivo seleccionado: {uploadFormData.selectedFile.name}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadFormData.selectedFile}
                style={{ 
                  background: (uploading || !uploadFormData.selectedFile) ? '#a0aec0' : '#38b2ac', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 4, 
                  padding: '8px 16px', 
                  fontWeight: 600, 
                  cursor: (uploading || !uploadFormData.selectedFile) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {uploading ? 'Subiendo...' : 'Subir documento'}
              </button>
              <button
                onClick={() => {
                  setShowUploadForm(false);
                  setError(null);
                  setUploadFormData({
                    report_type: 'medical',
                    sub_type: '',
                    description: '',
                    selectedFile: null
                  });
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                style={{ 
                  background: '#e2e8f0', 
                  color: '#4a5568', 
                  border: 'none', 
                  borderRadius: 4, 
                  padding: '8px 16px', 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentManager; 